import type { StaffImportBulkReviewBody } from "@lafayette/schemas";
import { useState } from "react";
import { actionRow, hintText } from "../../../catalog/components/styles";
import { Button } from "../../../ui/button";
import { CommandDialog } from "../../../ui/command-dialog";
import { SectionCard } from "../../../ui/section-card";
import type { SaveMessage } from "../../commands";
import { describeReviewFailure, type ReviewCommands } from "../commands";
import { plural } from "../labels";

type BulkAction = Readonly<{
  select: StaffImportBulkReviewBody["select"];
  outcome: StaffImportBulkReviewBody["outcome"];
  button: string;
  title: string;
  text: string;
  confirm: string;
}>;

const bulkActions: readonly BulkAction[] = [
  {
    select: "READY_AND_RESOLVED",
    outcome: "APPROVED",
    button: "Approve ready rows and strong matches",
    title: "Approve ready rows and strong matches?",
    text: "This approves rows that are ready, rows with a warning and no open decision, and rows that match an existing item by provider id or source key (they update that item). A weak match (same name and place, or a similar name), an invalid row and a row with an open decision are never approved here. They stay as they are and are counted.",
    confirm: "Approve these rows",
  },
  {
    select: "STRONG_UNCHANGED",
    outcome: "SKIPPED",
    button: "Skip unchanged matches",
    title: "Skip unchanged matches?",
    text: "This skips rows that match an existing item by provider id or source key and would change nothing. Other rows stay as they are and are counted.",
    confirm: "Skip these rows",
  },
  {
    select: "INVALID",
    outcome: "SKIPPED",
    button: "Skip invalid rows",
    title: "Skip invalid rows?",
    text: "This skips every invalid row. Nothing is imported from them. To import them, fix the file and import it again. Other rows stay as they are and are counted.",
    confirm: "Skip invalid rows",
  },
];

type BulkBarProps = Readonly<{
  commands: ReviewCommands;
  onMessage: (message: SaveMessage | null) => void;
}>;

/** The three bulk actions. Each asks first. A bulk review never takes a weak match. */
export function BulkBar({ commands, onMessage }: BulkBarProps) {
  const [confirming, setConfirming] = useState<BulkAction | null>(null);
  return (
    <SectionCard id="import-bulk" title="Bulk actions">
      <p className={hintText}>
        Each action tells you how many rows it left alone. A weak duplicate always needs your own
        decision.
      </p>
      <div className={actionRow}>
        {bulkActions.map((action) => (
          <Button
            key={action.select + action.outcome}
            type="button"
            variant={action.outcome === "APPROVED" ? "default" : "secondary"}
            disabled={commands.pending}
            onClick={() => setConfirming(action)}
          >
            {action.button}
          </Button>
        ))}
      </div>
      {confirming === null ? null : (
        <BulkDialog
          action={confirming}
          commands={commands}
          onMessage={onMessage}
          onClose={() => setConfirming(null)}
        />
      )}
    </SectionCard>
  );
}

function BulkDialog({
  action,
  commands,
  onMessage,
  onClose,
}: Readonly<{
  action: BulkAction;
  commands: ReviewCommands;
  onMessage: (message: SaveMessage | null) => void;
  onClose: () => void;
}>) {
  return (
    <CommandDialog
      title={action.title}
      description={action.text}
      confirmLabel={action.confirm}
      order="confirm-first"
      mutate={async () => {
        const version = commands.jobVersion();
        if (version === null) {
          return {
            ok: false,
            failure: {
              message: "The import could not be read. Refresh the page.",
              retryable: false,
              keepForm: true,
            },
          };
        }
        const outcome = await commands.bulk({
          expectedJobVersion: version,
          select: action.select,
          outcome: action.outcome,
        });
        if (!outcome.ok) {
          return {
            ok: false,
            failure: { message: describeReviewFailure(outcome), retryable: false, keepForm: true },
          };
        }
        return { ok: true, value: outcome.data };
      }}
      onDone={({ approvedCount, skippedCount, notEligibleCount }) => {
        onMessage({
          tone: "success",
          text: `${plural(approvedCount, "row", "rows")} approved, ${plural(skippedCount, "row", "rows")} skipped. ${plural(notEligibleCount, "row was", "rows were")} not eligible and left as ${notEligibleCount === 1 ? "it was" : "they were"}.`,
        });
        onClose();
      }}
      onClose={onClose}
    />
  );
}
